import i from 'i18next'
import { Fragment } from 'react'
import { motion } from 'framer-motion'
import slugify from 'slugify'
import { ProductDetail } from 'lib/hooks/use-products'
import { RadioEmpty, RadioFilled } from 'components/svg/Radio'
import { H1 } from 'components/sections/Header'
import { headers as ease } from 'constants/easings'
import Check from 'components/svg/Check'
import Rating from './Rating'
import Slider from './Slider'
import { BENELUX_FREE } from 'components/flyouts/Cart'
import BuyButton from './BuyButton'

// This should be temporary: https://github.com/wombak/scroller-motion/issues/3
export const fixPageNav = ( e ) => {
	e.preventDefault()
	const anchor = e.currentTarget.getAttribute( 'href' ).substr( 1 )
	window.scrollTo({
		top: document.getElementById( anchor ).offsetTop,
		behavior: 'smooth',
	})
}

type Props = {
	product: Pick<ProductDetail, 'title'|'descriptionHtml'|'options'|'images'> & {
		metafields?: ProductDetail['metafields']
	}
	variant: ProductDetail[ 'variants' ][ 0 ]
	reviews: number
	rating: number
	options: object
	setOptions: ( object ) => void
	animate?: boolean
	secondaryButton?: JSX.Element
}

export default function Detail({
	product,
	variant,
	reviews,
	rating,
	options,
	setOptions,
	animate = true,
	secondaryButton,
}: Props ) {
	const isSelected = ( name, value ) => (
		options[ name ] === slugify( value, { lower: true })
	)

	return (
		<header className="cf">
			<Slider product={product} variant={variant} />
			<H1 animate={animate} title={product.title} />
			<motion.div
				className="meta"
				initial={animate && { y: 80, opacity: 0 }}
				animate={{ y: 0, opacity: 1 }}
				exit={animate && {
					y: 50,
					opacity: 0,
					transition: { duration: 1, ease } }
				}
				transition={{ duration: 1, delay: .35, ease }}
			>
				{!!product.metafields?.[ 'volume-nl' ] && (
					<span>{product.metafields[ 'volume-nl' ]}</span>
				)}
				<a
					className="rating"
					href="#reviews"
					onClick={fixPageNav}
				>
					<Rating rating={rating} count={reviews} />
				</a>
			</motion.div>
			{/* <motion.div
					className="price"
					initial={{ y: 80, opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					exit={{ y: 50, opacity: 0, transition: { duration: 1, ease } }}
					transition={{ duration: 1, delay: .5, ease }}
				>
					{currency.format( variant.priceV2.amount )}
				</motion.div> */}
			<motion.div
				className="description"
				initial={animate && { y: 80, opacity: 0 }}
				animate={{ y: 0, opacity: 1 }}
				exit={animate && { y: 50, opacity: 0, transition: { duration: 1, ease } }}
				transition={{ duration: 1, delay: .5, ease }}
			>
				<div dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
				{/* <a
						className="cta"
						href="#details"
						onClick={fixPageNav}
					>
						{i.t( 'product.nav.more' )}
					</a> */}
			</motion.div>
			<motion.div
				transition={{ staggerChildren: .05, delayChildren: .85 }}
				initial={animate ? 'initial' : 'animate'}
				animate="animate"
				exit="exit"
				variants={{
					animate: {},
					exit: { opacity: 0, transition: { duration: 1, ease } },
				}}
			>
				{product.options
					.filter( ({ values }) => values.length > 1 )
					.map( ({ name, values }, i, { length }) => (
						<Fragment key={i}>
							<motion.div
								style={{
									transformOrigin: '0% 0%',
									height: 1,
									backgroundColor: '#d8dde3',
								}}
								variants={{
									initial: { scaleX: 0 },
									animate: { scaleX: 1 },
									exit: { opacity: 0 },
								}}
								transition={{ duration: 1, ease }}
							/>
							<motion.div
								key={name}
								role="radiogroup"
								// variants={{
								// 	initial: { opacity: 0 },
								// 	animate: { opacity: 1 },
								// }}
							>
								<motion.label
									variants={{
										initial: { opacity: 0 },
										animate: { opacity: 1 },
									}}
									transition={{ duration: 1, ease }}
								>
									{name}
								</motion.label>
								{values.map( value => (
									<motion.div
										key={value}
										role="radio"
										aria-checked={isSelected( name, value )}
										tabIndex={0}
										// onKeyDown={() => console.log( 'key down' )}
										onClick={() => setOptions( o => ({
											...o,
											[ name ]: slugify( value, { lower: true }),
										}) )}
										variants={{
											initial: { opacity: 0 },
											animate: { opacity: 1 },
										}}
										transition={{ duration: 1, ease }}
									>
										{( isSelected( name, value ) ?
											<RadioFilled />
											:
											<RadioEmpty />
										)}
										{value}
									</motion.div>
								) )}
							</motion.div>
							{i === length - 1 && (
								<motion.div
									style={{
										transformOrigin: '0% 0%',
										height: 1,
										backgroundColor: '#d8dde3',
									}}
									variants={{
										initial: { scaleX: 0 },
										animate: { scaleX: 1 },
										exit: { opacity: 0 },
									}}
									transition={{ duration: 1, ease }}
								/>
							)}
						</Fragment>
					) )}
			</motion.div>
			<motion.div
				className="buttons"
				initial={animate && { opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0, transition: { duration: 1, ease } }}
				transition={{ delay: 1, duration: .7, ease }}
			>
				<BuyButton variant={variant} />
				{secondaryButton}
			</motion.div>
			<motion.ul
				className="usps"
				initial={animate && { opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0, transition: { duration: 1, ease } }}
				transition={{ delay: 1, duration: .7, ease }}
			>
				<motion.li><Check />{i.t( 'usp.speed' )}</motion.li>
				<motion.li>
					<Check />
					{i.t( 'usp.free', { threshold: BENELUX_FREE })}
				</motion.li>
			</motion.ul>

			<style jsx>{`
				header {
					position: relative;
					display: grid;
					grid-template-columns: 5fr 4fr;
					grid-column-gap: 10vw;
				}
				header :global( .background ) {
					content: "";
					position: absolute;
					left: calc( 50% - 50vw );
					top: -1000px;
					right: calc( 50% - 50vw );
					bottom: calc( -50vh - 15vw );
					z-index: -1;
					background-color: #f7f7f7;
				}
				.back {
					display: block;
					padding: 20px;
					padding-left: 0;
					margin-bottom: 46px;
				}
				header :global( picture ) {
					grid-row: span 10;
				}
				header :global( legend ) {
					grid-column: 1;
					grid-row: 11;
				}
				header :global( .meta ) {
					display: flex;
					flex-wrap: wrap;
					align-items: center;
					color: #939aa0;
					font-size: 15px;
				}
				header :global( .meta > span ) {
					margin-right: 1em;
					padding-right: 1em;
					border-right: 1px solid #ddd;
				}
				header :global( a.rating ) {
					display: block;
					text-decoration: none;
				}
				header :global( .price ) {
					margin-top: .6em;
					font-size: 20px;
				}
				header :global( .description ) {
					margin-top: 50px;
					margin-bottom: 30px;
					font-size: 17px;
					line-height: 1.82;
				}
				header :global( .description > div > *:first-child ) {
					margin-top: 0;
				}
				header :global( .description p ) {
					margin: 0;
				}
				header :global( .description p + p ) {
					margin-top: 1em;
				}
				header :global( .description p > a:only-child ) {
					font-size: inherit;
					font-weight: inherit;
					text-decoration: revert;
				}
				header :global( .description ul ) {
					margin: 1em 0;
					padding-inline-start: 1em;
				}
				header :global( h1 ) {
					margin-top: 50px;
					line-height: 1.2;
					margin-bottom: .4em;
					text-transform: lowercase;
				}
				header :global( div[role="radiogroup"] ) {
					padding: 18px 0 26px;
					text-transform: lowercase;
					cursor: pointer;
				}
				header :global( div[role="radiogroup"] label ) {
					display: block;
					margin-bottom: .5em;
					font-size: 15px;
					color: #939aa0;
				}
				header :global( div[role="radio"] ) {
					margin-right: 28px;
					padding: 2px 8px;
					display: inline-flex;
					align-items: center;
					font-size: 20px;
					outline: none;
				}
				header :global( div[role="radio"] svg ) {
					transform: translateX( -8px );
				}
				header :global( button ) {
					margin-top: 30px;
					height: 80px;
					width: 100%;
					padding-left: 56px;
					padding-right: 56px;
					border: none;
					background-color: black;
					color: white;
					font-size: 18px;
					font-weight: 500;
					text-transform: lowercase;
					cursor: pointer;
				}
				header :global( button[disabled] ) {
					background-color: #F1F1F1;
					color: black;
					cursor: default;
				}
				header :global( .usps ) {
					margin-top: 20px;
				}
				header :global( .usps li ) {
					margin-bottom: .2em;
				}
				header :global( .usps li svg ) {
					margin-right: 1em;
				}

				@media ( max-width: 1100px ) {
					header {
						margin-top: 30px;
						grid-template-columns: 4fr 3fr;
					}
					header :global( picture ) {
						grid-column: 1;
					}
				}
				@media ( max-width: 900px ) {
					header {
						margin-top: 10px;
					}
					header :global( img ) {
					}
				}
				@media ( max-width: 720px ) {
					header {
						grid-template-columns: 1fr;
					}
					header {
						padding-bottom: 30px;
					}
					header :global( picture ) {
						height: 90vw;
						max-height: 60vh;
					}
					header :global( legend ) {
						display: none;
					}
					header :global( > * ) {
						order: 3;
					}
					header :global( h1 ) {
						order: 1;
						margin-top: 0;
					}
					header :global( .meta ) {
						order: 2;
						margin-bottom: 15px;
					}
					header :global( .description ) {
						margin-top: 20px;
					}
					header :global( button ) {
						width: 100%;
						padding-left: 16px;
						padding-right: 16px;
					}
				}
			`}</style>
		</header>
	)
}
